import { ArrowRight } from 'lucide-react'
import { Link, useSearchParams } from 'react-router-dom'

import { CLUB_SPORTS, SportxWordmark, findClubSport } from '@/components/auth/club-sports'
import SignupCompanyPage from '@/pages/auth/signup-company'

export default function SetupClubCompanyPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const selectedSport = findClubSport(searchParams.get('sport'))

  if (selectedSport) {
    return (
      <SignupCompanyPage
        signupPath="/setup-club-company"
        selectedSport={selectedSport.name}
        skipParentOrganizationPrompt
      />
    )
  }

  const chooseSport = (slug: string) => {
    const nextParams = new URLSearchParams(searchParams)
    nextParams.set('sport', slug)
    setSearchParams(nextParams)
    window.scrollTo({ top: 0 })
  }

  return (
    <div className="relative isolate min-h-screen overflow-hidden bg-[#0a1420] text-white">
      <div aria-hidden className="absolute -left-40 -top-40 -z-10 h-[32rem] w-[32rem] rounded-full bg-[#c9fa55]/10 blur-3xl" />
      <div aria-hidden className="absolute -bottom-48 -right-40 -z-10 h-[36rem] w-[36rem] rounded-full bg-sky-500/10 blur-3xl" />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-[0.06]"
        style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)', backgroundSize: '22px 22px' }}
      />

      <div className="mx-auto flex min-h-screen w-full max-w-6xl flex-col px-5 py-8 sm:px-8">
        <header className="flex items-center justify-between gap-4">
          <SportxWordmark />
          <p className="text-sm text-white/70">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-[#c9fa55] hover:underline">
              Sign in
            </Link>
          </p>
        </header>

        <main className="flex flex-1 flex-col justify-center py-12">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#c9fa55]">Set up your club</p>
            <h1 className="mt-4 text-4xl font-black leading-[1.05] tracking-tight sm:text-5xl">What does your club play?</h1>
            <p className="mt-4 text-base leading-7 text-white/70">
              Pick your main sport to get started. Your club workspace is ready in a couple of minutes.
            </p>
          </div>

          <ul className="mt-10 grid grid-cols-2 gap-3 sm:gap-5 lg:grid-cols-3">
            {CLUB_SPORTS.map((sport) => (
              <li key={sport.slug}>
                <button
                  type="button"
                  onClick={() => chooseSport(sport.slug)}
                  aria-label={`Set up a ${sport.name} club`}
                  className="group relative block aspect-[4/5] w-full overflow-hidden rounded-3xl bg-slate-800 text-left ring-1 ring-white/10 transition duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/40 hover:ring-[#c9fa55]/60 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#c9fa55] sm:aspect-[4/3]"
                >
                  <img
                    src={sport.image}
                    alt=""
                    className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  />
                  <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#0a1420] via-[#0a1420]/45 to-transparent" />
                  <span className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-4 sm:p-6">
                    <span className="min-w-0">
                      <span className="block text-lg font-black tracking-tight sm:text-2xl">{sport.name}</span>
                      <span className="mt-1 hidden text-sm leading-5 text-white/75 sm:line-clamp-2">{sport.intro}</span>
                    </span>
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white/15 ring-1 ring-white/25 backdrop-blur transition group-hover:bg-[#c9fa55] group-hover:text-[#0a1420] group-hover:ring-[#c9fa55]">
                      <ArrowRight className="h-4 w-4" />
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>

          <p className="mt-8 text-sm text-white/60">
            Not a sports club?{' '}
            <Link to="/signup" className="font-semibold text-white hover:underline">
              See other ways to sign up
            </Link>
          </p>
        </main>

        <footer className="text-[11px] leading-5 text-white/40">
          Photos: {CLUB_SPORTS.map((sport) => `${sport.name} — ${sport.credit}`).join(' · ')}
        </footer>
      </div>
    </div>
  )
}
